import { useEffect, useState } from 'react';
import useOpensStore from '@/stores/OpensStore';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { useShallow } from 'zustand/react/shallow';

import {
  convertToDisplayFormat,
  fromTipTapDoc,
  toTipTapDoc,
} from '@/lib/mentionsConvert';
import { hasConfigChanges, validateOpensConfig } from '@/lib/opensValidation';
import { saveOpensConfig } from '@/hooks/opens';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import MentionsEditor from '@/components/settings/canned-messages/editor/MentionsEditor';
import MessagePreview from '@/components/settings/canned-messages/editor/MessagePreview';

import { RadioGroup, RadioGroupItem } from '../ui/radio-group';

function ArrayStringEditor({
  label,
  values,
  onChange,
  placeholder,
  type = 'text',
  options = [],
  error,
}) {
  const [temp, setTemp] = useState('');

  const addValue = () => {
    const v = temp.trim();
    if (!v) return;
    onChange([...values, v]);
    setTemp('');
  };

  const removeValue = (index) => {
    const newValues = values.filter((_, i) => i !== index);
    onChange(newValues);
  };

  return (
    <div className='max-w-xl space-y-2'>
      <Label>{label}</Label>
      <div className='flex gap-2'>
        {type === 'select' ? (
          <Select value={temp} onValueChange={setTemp}>
            <SelectTrigger>
              <SelectValue placeholder={placeholder || 'Select option'} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={temp}
            onChange={(e) => setTemp(e.target.value)}
            placeholder={placeholder || 'Add value and click Add'}
          />
        )}
        <Button type='button' variant='outline' onClick={addValue}>
          Add
        </Button>
      </div>
      {Array.isArray(values) && values.length > 0 && (
        <div className='flex flex-wrap gap-2'>
          {values.map((v, idx) => (
            <div
              key={`${v}-${idx}`}
              className='flex items-center gap-1 rounded-md bg-secondary/20 px-2 py-1'
            >
              <span className='text-sm'>{v}</span>
              <Button
                type='button'
                variant='ghost'
                className='h-5 w-5 p-0'
                onClick={() => removeValue(idx)}
              >
                ×
              </Button>
            </div>
          ))}
        </div>
      )}
      {error && <p className='text-xs text-red-500'>{error}</p>}
    </div>
  );
}

function SortByEditor({ items, onChange }) {
  const sortFields = [
    { value: 'lead_score', label: 'Lead Score' },
    { value: 'created_at', label: 'Follower Followed Date' },
  ];
  const sortDirections = [
    { value: -1, label: 'Descending' },
    { value: 1, label: 'Ascending' },
  ];

  const addItem = () => {
    onChange([...items, { field: 'lead_score', direction: -1 }]);
  };

  const removeItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
  };

  const updateItem = (index, field, value) => {
    const newItems = items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    onChange(newItems);
  };

  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between'>
        {items.length < 2 && (
          <Button type='button' variant='outline' size='sm' onClick={addItem}>
            Add
          </Button>
        )}
      </div>
      <div className='space-y-2'>
        {(items || []).map((item, idx) => (
          <div key={idx} className='grid grid-cols-1 gap-2 sm:grid-cols-6'>
            <div className='sm:col-span-3'>
              <Select
                value={item.field}
                onValueChange={(v) => updateItem(idx, 'field', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sortFields.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='sm:col-span-2'>
              <Select
                value={String(item.direction)}
                onValueChange={(v) => updateItem(idx, 'direction', Number(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sortDirections.map((item) => (
                    <SelectItem key={item.value} value={String(item.value)}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='sm:col-span-1'>
              <Button
                type='button'
                variant='ghost'
                onClick={() => removeItem(idx)}
                className='w-full'
              >
                Remove
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MessageTemplatesEditor({
  templates,
  onChange,
  error,
  templateResponseRates,
}) {
  const [tempDoc, setTempDoc] = useState(() => toTipTapDoc(''));

  const addTemplate = () => {
    const t = fromTipTapDoc(tempDoc).trim();
    if (!t) return;

    const newTemplate = { id: uuidv4(), text: t };
    onChange([...templates, newTemplate]);
    setTempDoc(toTipTapDoc(''));
  };

  const removeTemplate = (index) => {
    // Don't allow removing if it's the last template
    if (templates.length === 1) {
      toast.error('At least one message template is required');
      return;
    }
    const newTemplates = templates.filter((_, i) => i !== index);
    onChange(newTemplates);
  };

  return (
    <div className='space-y-2'>
      {/* Preview of current message being typed */}
      <MessagePreview tipTapDoc={tempDoc} />

      <div className='flex gap-2'>
        <div className='flex-1'>
          <MentionsEditor
            value={tempDoc}
            onChange={setTempDoc}
            returnType='json'
            mentionSuggestions={['first-name']}
          />
        </div>
        <Button type='button' variant='outline' onClick={addTemplate}>
          Add
        </Button>
      </div>
      {templates.length > 0 && (
        <div className='space-y-2'>
          {templates.map((template, idx) => {
            const responseRate =
              template.template_text_hash &&
              templateResponseRates?.[template.template_text_hash];
            return (
              <div
                key={template.id}
                className='flex items-start justify-between rounded-md border p-2'
              >
                <div className='flex-1'>
                  <div className='mb-1 flex items-center gap-2 text-xs text-muted-foreground'>
                    <span>Template {idx + 1}</span>
                    {responseRate && (
                      <span className='rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700'>
                        {Math.round(responseRate.response_percentage)}% response
                        rate
                      </span>
                    )}
                  </div>
                  <div className='grid grid-cols-1 gap-2 lg:grid-cols-2'>
                    <div className='whitespace-pre-wrap break-words text-sm'>
                      {convertToDisplayFormat(template.text)}
                    </div>
                    <div className='border-l pl-2'>
                      <MessagePreview tipTapDoc={toTipTapDoc(template.text)} />
                    </div>
                  </div>
                </div>
                <Button
                  type='button'
                  variant='ghost'
                  onClick={() => removeTemplate(idx)}
                >
                  Remove
                </Button>
              </div>
            );
          })}
        </div>
      )}
      {error && <p className='text-xs text-red-500'>{error}</p>}
    </div>
  );
}

export default function OpensConfig() {
  const {
    enabled,
    config,
    proxySelectionIds,
    timezones,
    locationCountryCodes,
    updateConfig,
  } = useOpensStore(
    useShallow((s) => ({
      enabled: s.enabled,
      config: s.config,
      proxySelectionIds: s.proxySelectionIds,
      timezones: s.timezones,
      locationCountryCodes: s.locationCountryCodes,
      updateConfig: s.updateConfig,
    }))
  );

  // Gender options for predicted gender
  const genderOptions = [
    { value: 'M', label: 'Male' },
    { value: 'F', label: 'Female' },
  ];

  // Location options from country codes
  const locationOptions = Object.entries(locationCountryCodes).map(
    ([code, name]) => ({
      value: code,
      label: `${code} - ${name}`,
    })
  );

  // Required fields state
  const [proxySelectionId, setProxySelectionId] = useState('');
  const [maxDailyMessages, setMaxDailyMessages] = useState(0);
  const [minLeadScore, setMinLeadScore] = useState(0);
  const [timezone, setTimezone] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [sortByFields, setSortByFields] = useState([]);

  // Optional fields state
  const [minFollowers, setMinFollowers] = useState(0);
  const [isVerified, setIsVerified] = useState(null); // null means unset
  const [isPrivate, setIsPrivate] = useState(null); // null means unset
  const [includeBioKeywords, setIncludeBioKeywords] = useState([]);
  const [excludeBioKeywords, setExcludeBioKeywords] = useState([]);
  const [ensureValidProfilePic, setEnsureValidProfilePic] = useState(null); // null means unset
  const [includePredictedLocation, setIncludePredictedLocation] = useState([]);
  const [excludePredictedLocation, setExcludePredictedLocation] = useState([]);
  const [includePredictedGender, setIncludePredictedGender] = useState([]);
  const [excludePredictedGender, setExcludePredictedGender] = useState([]);
  const [includeUsernameKeywords, setIncludeUsernameKeywords] = useState([]);
  const [excludeUsernameKeywords, setExcludeUsernameKeywords] = useState([]);
  const [includeFullNameKeywords, setIncludeFullNameKeywords] = useState([]);
  const [excludeFullNameKeywords, setExcludeFullNameKeywords] = useState([]);
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [templateResponseRates, setTemplateResponseRates] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Load data from store on mount
  useEffect(() => {
    if (config) {
      setProxySelectionId(config.proxy_selection_id || '');
      setMaxDailyMessages(config.max_daily_messages || 0);
      setMinLeadScore(config.min_lead_score || 0);
      setTimezone(config.opens_window?.timezone || '');
      setStartTime(config.opens_window?.start_time || '');
      setEndTime(config.opens_window?.end_time || '');
      setSortByFields(config.followers_selection_filters?.sort_by_fields || []);

      // Optional fields
      setMinFollowers(config.followers_selection_filters?.min_followers || 0);
      setIsVerified(
        typeof config.followers_selection_filters?.is_verified === 'boolean'
          ? config.followers_selection_filters?.is_verified
          : null
      );
      setIsPrivate(
        typeof config.followers_selection_filters?.is_private === 'boolean'
          ? config.followers_selection_filters?.is_private
          : null
      );
      setIncludeBioKeywords(
        config.followers_selection_filters?.include_bio_keywords || []
      );
      setExcludeBioKeywords(
        config.followers_selection_filters?.exclude_bio_keywords || []
      );
      setEnsureValidProfilePic(
        typeof config.followers_selection_filters?.ensure_valid_profile_pic ===
          'boolean'
          ? config.followers_selection_filters?.ensure_valid_profile_pic
          : null
      );
      setIncludePredictedLocation(
        config.followers_selection_filters?.include_predicted_location || []
      );
      setExcludePredictedLocation(
        config.followers_selection_filters?.exclude_predicted_location || []
      );
      setIncludePredictedGender(
        config.followers_selection_filters?.include_predicted_gender || []
      );
      setExcludePredictedGender(
        config.followers_selection_filters?.exclude_predicted_gender || []
      );
      setIncludeUsernameKeywords(
        config.followers_selection_filters?.include_username_keywords || []
      );
      setExcludeUsernameKeywords(
        config.followers_selection_filters?.exclude_username_keywords || []
      );
      setIncludeFullNameKeywords(
        config.followers_selection_filters?.include_full_name_keywords || []
      );
      setExcludeFullNameKeywords(
        config.followers_selection_filters?.exclude_full_name_keywords || []
      );

      // Load message templates, ensuring at least one template exists
      const templates =
        config.stage_config?.message_generator?.message_selection
          ?.message_templates || [];
      if (templates.length === 0 && config.message_template) {
        // Pre-fill with default message template
        setMessageTemplates([{ id: 'msg_1', text: config.message_template }]);
      } else {
        setMessageTemplates(templates);
      }

      setTemplateResponseRates(config.template_response_rates || {});
    }
  }, [config]);

  // Detect changes by comparing current form state with stored config
  useEffect(() => {
    const formState = {
      proxySelectionId,
      maxDailyMessages,
      minLeadScore,
      timezone,
      startTime,
      endTime,
      sortByFields,
      minFollowers,
      isVerified,
      isPrivate,
      includeBioKeywords,
      excludeBioKeywords,
      ensureValidProfilePic,
      includePredictedLocation,
      excludePredictedLocation,
      includePredictedGender,
      excludePredictedGender,
      includeUsernameKeywords,
      excludeUsernameKeywords,
      includeFullNameKeywords,
      excludeFullNameKeywords,
      messageTemplates,
    };

    const changes = hasConfigChanges(config, formState);
    setHasChanges(changes);
  }, [
    config,
    proxySelectionId,
    maxDailyMessages,
    minLeadScore,
    timezone,
    startTime,
    endTime,
    sortByFields,
    minFollowers,
    isVerified,
    isPrivate,
    includeBioKeywords,
    excludeBioKeywords,
    ensureValidProfilePic,
    includePredictedLocation,
    excludePredictedLocation,
    includePredictedGender,
    excludePredictedGender,
    includeUsernameKeywords,
    excludeUsernameKeywords,
    includeFullNameKeywords,
    excludeFullNameKeywords,
    messageTemplates,
  ]);

  const handleSave = async () => {
    setIsLoading(true);
    setValidationErrors({});

    try {
      // Build followers selection filters conditionally
      const followersFilters = {
        ...(sortByFields.length > 0 && { sort_by_fields: sortByFields }),
      };

      if (minFollowers > 0) followersFilters.min_followers = minFollowers;

      if (isVerified !== null) followersFilters.is_verified = isVerified;
      if (isPrivate !== null) followersFilters.is_private = isPrivate;
      if (ensureValidProfilePic !== null)
        followersFilters.ensure_valid_profile_pic = ensureValidProfilePic;

      if (includeBioKeywords.length > 0)
        followersFilters.include_bio_keywords = includeBioKeywords;
      if (excludeBioKeywords.length > 0)
        followersFilters.exclude_bio_keywords = excludeBioKeywords;
      if (includePredictedLocation.length > 0)
        followersFilters.include_predicted_location = includePredictedLocation;
      if (excludePredictedLocation.length > 0)
        followersFilters.exclude_predicted_location = excludePredictedLocation;
      if (includePredictedGender.length > 0)
        followersFilters.include_predicted_gender = includePredictedGender;
      if (excludePredictedGender.length > 0)
        followersFilters.exclude_predicted_gender = excludePredictedGender;
      if (includeUsernameKeywords.length > 0)
        followersFilters.include_username_keywords = includeUsernameKeywords;
      if (excludeUsernameKeywords.length > 0)
        followersFilters.exclude_username_keywords = excludeUsernameKeywords;
      if (includeFullNameKeywords.length > 0)
        followersFilters.include_full_name_keywords = includeFullNameKeywords;
      if (excludeFullNameKeywords.length > 0)
        followersFilters.exclude_full_name_keywords = excludeFullNameKeywords;

      // Get the first message template as the default message_template
      const defaultMessageTemplate =
        messageTemplates.length > 0 ? messageTemplates[0].text : '';

      const configData = {
        enabled: enabled ?? false, // Include current enabled state
        proxy_selection_id: proxySelectionId,
        max_daily_messages: maxDailyMessages,
        min_lead_score: minLeadScore,
        message_template: defaultMessageTemplate,
        opens_window: {
          timezone,
          start_time: startTime,
          end_time: endTime,
        },
        followers_selection_filters: followersFilters,
      };

      // Always include stage_config if templates are present
      if (messageTemplates.length > 0) {
        configData.stage_config = {
          message_generator: {
            message_selection: {
              enabled: true, // Always enabled
              selection_method: 'random_matched',
              message_templates: messageTemplates,
            },
          },
        };
      }

      // Validate all fields at once
      const validation = validateOpensConfig(configData);
      if (!validation.success) {
        const errors = {};
        validation.errors.forEach((error) => {
          // Handle array field paths by removing the array index
          const fieldPath = error.field.replace(/\.\d+$/, '');
          errors[fieldPath] = error.message;
        });
        setValidationErrors(errors);
        toast.error('Please fix the validation errors');
        return;
      }

      await toast.promise(saveOpensConfig(configData), {
        loading: 'Saving configuration...',
        success: () => {
          // Separate enabled from config data before storing
          // eslint-disable-next-line no-unused-vars
          const { enabled, ...configWithoutEnabled } = configData;
          updateConfig(configWithoutEnabled);
          setHasChanges(false); // Reset changes after successful save
          return 'Configuration saved successfully!';
        },
        error: (error) => {
          return (
            error.response?.data?.message || 'Failed to save configuration'
          );
        },
      });
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='space-y-8 p-1'>
      {/* Sticky Header */}
      <div className='sticky top-0 z-10 border-b bg-background p-4 shadow-sm'>
        <div className='flex items-center justify-between'>
          <div>
            <Label className='text-center text-base font-medium'>
              Opens Configuration
            </Label>
            <div className='text-xs text-muted-foreground'>
              Configure automated opens for your followers.
            </div>
          </div>

          <div className='flex items-center justify-center'>
            <Button
              size='sm'
              onClick={handleSave}
              disabled={isLoading || !hasChanges}
            >
              {isLoading ? 'Saving...' : 'Save Configuration'}
            </Button>
          </div>
        </div>
      </div>

      <div className='space-y-6'>
        {/* 1. Follower Filters */}
        <div className='space-y-4 rounded-md border p-4'>
          <div className='mb-2'>
            <div className='text-sm font-semibold'>Follower Filters</div>
            <div className='text-xs text-muted-foreground'>
              Configure filters to select which followers to send opens to.
            </div>
          </div>

          <div className='grid grid-cols-1 gap-4'>
            <div className='flex items-center space-x-2'>
              <Label className='min-w-[240px]'>Is Verified</Label>
              <RadioGroup
                className='flex items-center gap-4'
                value={
                  isVerified === null ? 'any' : isVerified ? 'true' : 'false'
                }
                onValueChange={(v) =>
                  setIsVerified(v === 'any' ? null : v === 'true')
                }
              >
                <Label className='flex items-center gap-2'>
                  <RadioGroupItem value='any' /> Any
                </Label>
                <Label className='flex items-center gap-2'>
                  <RadioGroupItem value='true' /> Yes
                </Label>
                <Label className='flex items-center gap-2'>
                  <RadioGroupItem value='false' /> No
                </Label>
              </RadioGroup>
            </div>
            <div className='flex items-center space-x-2'>
              <Label className='min-w-[240px]'>Is Private Account</Label>
              <RadioGroup
                className='flex items-center gap-4'
                value={
                  isPrivate === null ? 'any' : isPrivate ? 'true' : 'false'
                }
                onValueChange={(v) =>
                  setIsPrivate(v === 'any' ? null : v === 'true')
                }
              >
                <Label className='flex items-center gap-2'>
                  <RadioGroupItem value='any' /> Any
                </Label>
                <Label className='flex items-center gap-2'>
                  <RadioGroupItem value='true' /> Yes
                </Label>
                <Label className='flex items-center gap-2'>
                  <RadioGroupItem value='false' /> No
                </Label>
              </RadioGroup>
            </div>
            <div className='flex items-center space-x-2'>
              <Label className='min-w-[240px]'>Ensure valid profile pic</Label>
              <RadioGroup
                className='flex items-center gap-4'
                value={
                  ensureValidProfilePic === null
                    ? 'any'
                    : ensureValidProfilePic
                      ? 'true'
                      : 'false'
                }
                onValueChange={(v) =>
                  setEnsureValidProfilePic(v === 'any' ? null : v === 'true')
                }
              >
                <Label className='flex items-center gap-2'>
                  <RadioGroupItem value='any' /> Any
                </Label>
                <Label className='flex items-center gap-2'>
                  <RadioGroupItem value='true' /> Yes
                </Label>
                <Label className='flex items-center gap-2'>
                  <RadioGroupItem value='false' /> No
                </Label>
              </RadioGroup>
            </div>
          </div>

          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <div className='space-y-1'>
              <Label>
                Min lead score <span className='text-red-500'>*</span>
              </Label>
              <div className='text-xs text-muted-foreground'>
                Minimum lead score to send opens to. (Choose between -100 and
                100)
              </div>
              <Input
                type='number'
                min={-100}
                max={100}
                value={minLeadScore}
                onChange={(e) => setMinLeadScore(Number(e.target.value))}
                placeholder='e.g. 0'
              />
              {validationErrors.min_lead_score && (
                <p className='text-xs text-red-500'>
                  {validationErrors.min_lead_score}
                </p>
              )}
            </div>

            <div className='space-y-1'>
              <Label>Minimum followers</Label>
              <div className='text-xs text-muted-foreground'>
                The minimum number of followers the follower must have.
              </div>
              <Input
                type='number'
                min={0}
                value={minFollowers}
                onChange={(e) => setMinFollowers(Number(e.target.value))}
                placeholder='e.g. 500'
              />
            </div>
          </div>

          <div className='grid grid-cols-1 gap-6'>
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <ArrayStringEditor
                label='Include Bio Keywords'
                values={includeBioKeywords}
                onChange={setIncludeBioKeywords}
                error={
                  validationErrors[
                    'followers_selection_filters.include_bio_keywords'
                  ]
                }
              />
              <ArrayStringEditor
                label='Exclude Bio Keywords'
                values={excludeBioKeywords}
                onChange={setExcludeBioKeywords}
                error={
                  validationErrors[
                    'followers_selection_filters.exclude_bio_keywords'
                  ]
                }
              />
            </div>

            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <ArrayStringEditor
                label='Include Predicted Location'
                values={includePredictedLocation}
                onChange={setIncludePredictedLocation}
                type='select'
                options={locationOptions}
                placeholder='Select country code'
                error={
                  validationErrors[
                    'followers_selection_filters.include_predicted_location'
                  ]
                }
              />
              <ArrayStringEditor
                label='Exclude Predicted Location'
                values={excludePredictedLocation}
                onChange={setExcludePredictedLocation}
                type='select'
                options={locationOptions}
                placeholder='Select country code'
                error={
                  validationErrors[
                    'followers_selection_filters.exclude_predicted_location'
                  ]
                }
              />
            </div>

            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <ArrayStringEditor
                label='Include Predicted Gender'
                values={includePredictedGender}
                onChange={setIncludePredictedGender}
                type='select'
                options={genderOptions}
                placeholder='Select gender'
                error={
                  validationErrors[
                    'followers_selection_filters.include_predicted_gender'
                  ]
                }
              />
              <ArrayStringEditor
                label='Exclude Predicted Gender'
                values={excludePredictedGender}
                onChange={setExcludePredictedGender}
                type='select'
                options={genderOptions}
                placeholder='Select gender'
                error={
                  validationErrors[
                    'followers_selection_filters.exclude_predicted_gender'
                  ]
                }
              />
            </div>

            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <ArrayStringEditor
                label='Include Username Keywords'
                values={includeUsernameKeywords}
                onChange={setIncludeUsernameKeywords}
                error={
                  validationErrors[
                    'followers_selection_filters.include_username_keywords'
                  ]
                }
              />
              <ArrayStringEditor
                label='Exclude Username Keywords'
                values={excludeUsernameKeywords}
                onChange={setExcludeUsernameKeywords}
                error={
                  validationErrors[
                    'followers_selection_filters.exclude_username_keywords'
                  ]
                }
              />
            </div>

            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <ArrayStringEditor
                label='Include Full Name Keywords'
                values={includeFullNameKeywords}
                onChange={setIncludeFullNameKeywords}
                error={
                  validationErrors[
                    'followers_selection_filters.include_full_name_keywords'
                  ]
                }
              />
              <ArrayStringEditor
                label='Exclude Full Name Keywords'
                values={excludeFullNameKeywords}
                onChange={setExcludeFullNameKeywords}
                error={
                  validationErrors[
                    'followers_selection_filters.exclude_full_name_keywords'
                  ]
                }
              />
            </div>
          </div>
        </div>

        {/* 2. Messages */}
        <div className='space-y-4 rounded-md border p-4'>
          <div className='mb-2 space-y-1'>
            <div className='text-sm font-semibold'>
              Messages <span className='text-red-500'>*</span>
            </div>
            <div className='flex flex-col gap-2 text-xs text-muted-foreground'>
              <span>
                Define message to send to followers. At least one is required.
                If more than one message is provided, the messages will be
                picked in a random order.
              </span>
              <span>
                You can use <code>@first-name</code> to add first name in your
                message
              </span>
            </div>
          </div>

          <MessageTemplatesEditor
            templates={messageTemplates}
            onChange={setMessageTemplates}
            templateResponseRates={templateResponseRates}
            error={
              validationErrors[
                'stage_config.message_generator.message_selection.message_templates'
              ]
            }
          />
          {validationErrors.message_template && (
            <p className='text-xs text-red-500'>
              {validationErrors.message_template}
            </p>
          )}
        </div>

        {/* 3. Opens Window */}
        <div className='space-y-4 rounded-md border p-4'>
          <div className='mb-2'>
            <div className='text-sm font-semibold'>Opens Window</div>
            <div className='text-xs text-muted-foreground'>
              The time window for automated opens (24 hour format)
            </div>
          </div>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
            <div className='space-y-1'>
              <Label>
                Timezone <span className='text-red-500'>*</span>
              </Label>
              <div className='text-xs text-muted-foreground'>
                Please select the timezone for the opens window.
              </div>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger>
                  <SelectValue placeholder='Select timezone' />
                </SelectTrigger>
                <SelectContent>
                  {timezones ? (
                    timezones.map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz}
                      </SelectItem>
                    ))
                  ) : (
                    <div className='px-2 py-1 text-xs text-muted-foreground'>
                      No timezones available
                    </div>
                  )}
                </SelectContent>
              </Select>
              {validationErrors['opens_window.timezone'] && (
                <p className='text-xs text-red-500'>
                  {validationErrors['opens_window.timezone']}
                </p>
              )}
            </div>

            <div className='space-y-1'>
              <Label>
                Start Time <span className='text-red-500'>*</span>
              </Label>
              <div className='text-xs text-muted-foreground'>
                Please enter in HH:MM & start time should be after 00:15
              </div>
              <Input
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                placeholder='eg. 11:00'
              />
              {validationErrors['opens_window.start_time'] && (
                <p className='text-xs text-red-500'>
                  {validationErrors['opens_window.start_time']}
                </p>
              )}
            </div>

            <div className='space-y-1'>
              <Label>
                End Time <span className='text-red-500'>*</span>
              </Label>
              <div className='text-xs text-muted-foreground'>
                Please enter in HH:MM & end time should be before 23:45
              </div>
              <Input
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                placeholder='eg. 23:40'
              />
              {validationErrors['opens_window.end_time'] && (
                <p className='text-xs text-red-500'>
                  {validationErrors['opens_window.end_time']}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* 4. Proxy Location Selection */}
        <div className='space-y-4 rounded-md border p-4'>
          <div className='mb-2'>
            <div className='text-sm font-semibold'>
              Proxy Location Selection
            </div>
            <div className='text-xs text-muted-foreground'>
              Please choose the location nearest to you.
            </div>
          </div>
          <div className='max-w-md'>
            <Label>
              Proxy Location <span className='text-red-500'>*</span>
            </Label>
            <Select
              value={proxySelectionId}
              onValueChange={setProxySelectionId}
            >
              <SelectTrigger>
                <SelectValue placeholder='Select location' />
              </SelectTrigger>
              <SelectContent>
                {proxySelectionIds ? (
                  Object.entries(proxySelectionIds).map(([code, label]) => (
                    <SelectItem key={code} value={code}>
                      {label}
                    </SelectItem>
                  ))
                ) : (
                  <div className='px-2 py-1 text-xs text-muted-foreground'>
                    No locations available
                  </div>
                )}
              </SelectContent>
            </Select>
            {validationErrors.proxy_selection_id && (
              <p className='text-xs text-red-500'>
                {validationErrors.proxy_selection_id}
              </p>
            )}
          </div>
        </div>

        {/* 5. Daily Message Count */}
        <div className='space-y-4 rounded-md border p-4'>
          <div className='mb-2'>
            <div className='text-sm font-semibold'>Daily Message Count</div>
            <div className='text-xs text-muted-foreground'>
              Maximum number of messages to send per day.
            </div>
          </div>
          <div className='max-w-md'>
            <Label>
              Max Daily Messages <span className='text-red-500'>*</span>
            </Label>
            <Input
              type='number'
              min={0}
              max={1000}
              value={maxDailyMessages}
              onChange={(e) => setMaxDailyMessages(Number(e.target.value))}
              placeholder='e.g. 50'
            />
            {validationErrors.max_daily_messages && (
              <p className='text-xs text-red-500'>
                {validationErrors.max_daily_messages}
              </p>
            )}
          </div>
        </div>

        {/* 6. Follower Selection Order - Moved to bottom */}
        <div className='space-y-4 rounded-md border p-4'>
          <div className='mb-2'>
            <div className='text-sm font-semibold'>
              Follower Selection Order
            </div>
            <div className='text-xs text-muted-foreground'>
              Sort the selection of followers based on the selected fields.
            </div>
          </div>
          <SortByEditor items={sortByFields} onChange={setSortByFields} />
          {validationErrors['followers_selection_filters.sort_by_fields'] && (
            <p className='text-xs text-red-500'>
              {validationErrors['followers_selection_filters.sort_by_fields']}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
